import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import axiosJC261 from 'src/app/Service/axiosJC261';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import { SignalrImageService } from 'src/app/Service/signalr-image.service';
import { Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ImageRecord, MonitoringImage } from './cctv-monitoring.models';
import { normalizeImage } from './cctv-monitoring.utils';

@Component({
  selector: 'app-cctv-monitoring',
  providers: [SignalrImageService],
  templateUrl: './cctv-monitoring.component.html',
  styleUrls: ['./cctv-monitoring.component.css'],
})
export class CctvMonitoringComponent implements OnInit, AfterViewInit, OnDestroy
{
  // View elements
  @ViewChild('gallery') gallery!: ElementRef<HTMLElement>;
  @ViewChild('wall') wall?: ElementRef<HTMLElement>;
  @ViewChild('pagination') pagination?: ElementRef<HTMLElement>;
  @ViewChild('statusLine') statusLine!: ElementRef<HTMLElement>;

  // Images and filters
  images: MonitoringImage[] = [];
  category: 'cabin' | 'outside' = 'cabin';
  search = '';
  readonly pinned = new Set<string>();
  selectedImage: MonitoringImage | null = null;
  private readonly vehicleOrder = new Map<string, number>();

  // Pagination and layout
  readonly sizeChoices = [4, 6, 8, 12, 16, 24, 32, 48, 64, 128, 256];
  showStats = false;
  page = 1;
  pageSize = 4;
  gridColumns = 2;
  gridRows = 2;
  private automaticPageSize = true;
  private fittingPageSizes = this.sizeChoices;
  private resizeFrame?: number;
  private galleryResizeObserver?: ResizeObserver;

  // Rotation and timers
  rotationSeconds = 10;
  paused = false;
  ageTick = 0;
  private rotationTimer?: ReturnType<typeof setInterval>;
  private refreshTimer?: ReturnType<typeof setInterval>;
  private ageTimer?: ReturnType<typeof setInterval>;

  // Data loading and hub connection
  lastUpdated: Date | null = null;
  hasLoaded = false;
  loading = false;
  loadError = '';
  private pendingHubImages: ImageRecord[] = [];
  private destroy$ = new Subject<void>();

  // Image transitions
  private transitionFrame?: number;
  private transitionTimer?: ReturnType<typeof setTimeout>;
  private transitionOverlay?: HTMLElement;
  private transitionAnimations: Animation[] = [];

  constructor(
    private guard: GurdServiceService,
    private notification: NotificationService,
    private otherService: OtherService,
    private router: Router,
    private imageHubService: SignalrImageService,
  ) {}

  ngOnInit(): void {
    this.loadImages();
    this.restartRotation();
    this.ageTimer = setInterval(() => this.ageTick++, 30000);
    if (environment.imageUrlHub?.trim()) {
      this.imageHubService.isHubConnected$
        .pipe(takeUntil(this.destroy$))
        .subscribe((connected) => {
          if (connected) {
            if (this.refreshTimer) {
              clearInterval(this.refreshTimer);
              this.refreshTimer = undefined;
              this.loadImages();
            }
          } else if (!this.refreshTimer) {
            this.refreshTimer = setInterval(() => this.loadImages(), 60000);
          }
        });
      this.imageHubService.hubError$
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          console.error('HC_HubError', data);
        });
      this.imageHubService.mobileImageReceived$
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          if (this.loading) this.pendingHubImages.push(data);
          this.updateHubImage(data);
        });
      this.imageHubService.init();
    } else {
      this.refreshTimer = setInterval(() => this.loadImages(), 60000);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.imageHubService.disconnect();
    if (this.rotationTimer) clearInterval(this.rotationTimer);
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    if (this.ageTimer) clearInterval(this.ageTimer);
    this.clearTransition();
    if (this.resizeFrame !== undefined) cancelAnimationFrame(this.resizeFrame);
    this.galleryResizeObserver?.disconnect();
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.galleryResizeObserver = new ResizeObserver(() =>
        this.schedulePageSizeUpdate(),
      );
      this.galleryResizeObserver.observe(this.gallery.nativeElement);
    }
    this.schedulePageSizeUpdate();
  }

  @HostListener('window:resize')
  schedulePageSizeUpdate(): void {
    if (this.resizeFrame !== undefined) cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = undefined;
      this.updatePageSize();
    });
  }

  toggleStats(): void {
    this.showStats = !this.showStats;
    this.schedulePageSizeUpdate();
  }

  private updatePageSize(): void {
    if (!this.gallery || !this.statusLine) return;
    const gallery = this.gallery.nativeElement;
    const width = Math.max(1, gallery.clientWidth - 24);
    const height = Math.max(
      1,
      gallery.clientHeight -
        this.statusLine.nativeElement.offsetHeight -
        (this.pagination?.nativeElement.offsetHeight || 48) -
        24,
    );
    this.fittingPageSizes = this.sizeChoices.filter((size) =>
      this.findLayout(size, width, height, true),
    );
    if (!this.fittingPageSizes.length) this.fittingPageSizes = [4];
    const options = this.fittingPageSizes;
    const desired = (width * height) / (400 * 260);
    let nextSize = this.automaticPageSize
      ? options.reduce(
          (best, size) =>
            Math.abs(Math.log(size / desired)) <
            Math.abs(Math.log(best / desired))
              ? size
              : best,
          options[0],
        )
      : options.includes(this.pageSize)
        ? this.pageSize
        : options.filter((size) => size <= this.pageSize).pop() || options[0];
    if (nextSize < this.pinned.size) {
      nextSize =
        options.find((size) => size >= this.pinned.size) || this.pageSize;
    }
    if (nextSize !== this.pageSize) {
      this.captureTransition();
      this.pageSize = nextSize;
      this.page = 1;
    }
    const layout =
      this.findLayout(this.pageSize, width, height, true) ||
      this.findLayout(this.pageSize, width, height, false);
    if (layout) {
      this.gridColumns = layout.columns;
      this.gridRows = layout.rows;
    }
  }

  private findLayout(
    count: number,
    width: number,
    height: number,
    requireFit: boolean,
  ): { columns: number; rows: number } | null {
    let best: { columns: number; rows: number; score: number } | null = null;
    for (let columns = 1; columns <= count; columns++) {
      const rows = Math.ceil(count / columns);
      const tileWidth = (width - (columns - 1) * 5) / columns;
      const tileHeight = (height - (rows - 1) * 5) / rows;
      if (
        tileWidth <= 0 ||
        tileHeight <= 0 ||
        (requireFit && (tileWidth < 145 || tileHeight < 105))
      )
        continue;
      const score =
        -Math.abs(Math.log(tileWidth / tileHeight / 1.55)) -
        (columns * rows - count) * 0.09;
      if (!best || score > best.score) best = { columns, rows, score };
    }
    return best;
  }

  private filterImages(images: MonitoringImage[]): MonitoringImage[] {
    const query = this.search.trim().toLowerCase();
    return images.filter(
      (image) =>
        image.category === this.category &&
        (!query ||
          [image.name, image.plate, image.unitId].some((value) =>
            value.toLowerCase().includes(query),
          )),
    );
  }

  get filteredImages(): MonitoringImage[] {
    return this.filterImages(this.images);
  }

  private visibleFor(
    images: MonitoringImage[],
    pinnedIds: Set<string>,
    page: number,
  ): MonitoringImage[] {
    const filtered = this.filterImages(images);
    const pinned = filtered
      .filter((image) => pinnedIds.has(image.unitId))
      .slice(0, this.pageSize);
    const others = filtered.filter((image) => !pinnedIds.has(image.unitId));
    const slots = this.pageSize - pinned.length;
    return [...pinned, ...others.slice((page - 1) * slots, page * slots)];
  }

  private pageCountFor(
    images: MonitoringImage[],
    pinnedIds: Set<string>,
  ): number {
    const filtered = this.filterImages(images);
    const pinned = filtered.filter((image) =>
      pinnedIds.has(image.unitId),
    ).length;
    const slots = this.pageSize - pinned;
    return slots > 0
      ? Math.max(1, Math.ceil((filtered.length - pinned) / slots))
      : 1;
  }

  get visibleImages(): MonitoringImage[] {
    return this.visibleFor(this.images, this.pinned, this.page);
  }

  get pageCount(): number {
    return this.pageCountFor(this.images, this.pinned);
  }

  private captureIfVisibleChanges(
    images: MonitoringImage[],
    pinnedIds: Set<string>,
    page: number,
  ): void {
    const current = this.visibleImages;
    const next = this.visibleFor(images, pinnedIds, page);
    if (
      current.length !== next.length ||
      current.some((image, index) => {
        const replacement = next[index];
        return (
          image.unitId !== replacement.unitId ||
          image.category !== replacement.category ||
          image.thumbnailUrl !== replacement.thumbnailUrl
        );
      })
    )
      this.captureTransition();
  }

  get pageSizeOptions(): number[] {
    const options = [...this.fittingPageSizes];
    if (!options.includes(this.pageSize)) options.push(this.pageSize);
    return options.sort((a, b) => a - b);
  }

  togglePin(image: MonitoringImage): void {
    const pinnedIds = new Set(this.pinned);
    if (pinnedIds.has(image.unitId)) pinnedIds.delete(image.unitId);
    else if (pinnedIds.size >= this.pageSize) {
      this.notification.showWarning(
        'تعداد خودروهای پین‌شده نمی‌تواند بیشتر از تعداد تصاویر صفحه باشد.',
        'پین تصویر',
      );
      return;
    } else pinnedIds.add(image.unitId);
    const nextPage = Math.min(
      this.page,
      this.pageCountFor(this.images, pinnedIds),
    );
    this.captureIfVisibleChanges(this.images, pinnedIds, nextPage);
    if (pinnedIds.has(image.unitId)) this.pinned.add(image.unitId);
    else this.pinned.delete(image.unitId);
    this.page = nextPage;
    this.restartRotation();
  }

  setPageSize(size: number): void {
    if (size < this.pinned.size) {
      this.notification.showWarning(
        'ابتدا پین خودروهای اضافی را بردارید.',
        'تعداد تصاویر',
      );
      return;
    }
    this.captureTransition();
    this.pageSize = size;
    this.automaticPageSize = false;
    this.page = 1;
    this.restartRotation();
    this.schedulePageSizeUpdate();
  }

  openImage(image: MonitoringImage): void {
    this.selectedImage = image;
  }

  closeImage(): void {
    this.selectedImage = null;
  }

  restartRotation(): void {
    if (this.rotationTimer) clearInterval(this.rotationTimer);
    if (this.rotationSeconds > 0)
      this.rotationTimer = setInterval(() => {
        if (
          !this.paused &&
          !this.selectedImage &&
          !this.search.trim() &&
          this.pageCount > 1
        ) {
          this.setPage(this.page >= this.pageCount ? 1 : this.page + 1, false);
        }
      }, this.rotationSeconds * 1000);
  }

  setPage(page: number, manual = true): void {
    if (page === this.page) return;
    this.captureIfVisibleChanges(this.images, this.pinned, page);
    this.page = page;
    if (manual) this.restartRotation();
  }

  onSearchChange(): void {
    this.captureTransition();
    this.page = 1;
  }

  private clearTransition(): void {
    if (this.transitionFrame !== undefined)
      cancelAnimationFrame(this.transitionFrame);
    if (this.transitionTimer) clearTimeout(this.transitionTimer);
    this.transitionAnimations.forEach((animation) => animation.cancel());
    this.transitionAnimations = [];
    this.transitionOverlay?.remove();
    this.transitionOverlay = undefined;
    this.transitionFrame = undefined;
    this.transitionTimer = undefined;
  }

  private captureTransition(tileIndex?: number): void {
    this.clearTransition();
    const wall = this.wall?.nativeElement;
    if (!wall || window.matchMedia('(prefers-reduced-motion: reduce)').matches)
      return;
    const tiles = Array.from(
      wall.querySelectorAll<HTMLElement>(':scope > app-image-tile'),
    );
    if (!tiles.length) return;
    const oldKeys = tiles.map((tile) => tile.dataset.imageKey || '');
    const bounds = wall.getBoundingClientRect();
    const overlay = document.createElement('div');
    overlay.className = 'transition-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    tiles.forEach((tile, index) => {
      if (tileIndex !== undefined && index !== tileIndex) return;
      const rect = tile.getBoundingClientRect();
      const copy = tile.cloneNode(true) as HTMLElement;
      copy
        .querySelectorAll<HTMLElement>('a, button, [tabindex]')
        .forEach((element) => (element.tabIndex = -1));
      Object.assign(copy.style, {
        position: 'absolute',
        left: `${rect.left - bounds.left}px`,
        top: `${rect.top - bounds.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        margin: '0',
      });
      overlay.appendChild(copy);
    });
    wall.appendChild(overlay);
    this.transitionOverlay = overlay;
    this.transitionFrame = requestAnimationFrame(() => {
      this.transitionFrame = undefined;
      const currentWall = this.wall?.nativeElement;
      const newKeys = currentWall
        ? Array.from(
            currentWall.querySelectorAll<HTMLElement>(
              ':scope > app-image-tile',
            ),
          ).map((tile) => tile.dataset.imageKey || '')
        : [];
      if (
        currentWall !== wall ||
        (oldKeys.length === newKeys.length &&
          oldKeys.every((key, index) => key === newKeys[index]))
      ) {
        this.clearTransition();
        return;
      }
      this.transitionAnimations = Array.from(overlay.children).map(
        (copy, index) =>
          (copy as HTMLElement).animate([{ opacity: 1 }, { opacity: 0 }], {
            duration: 1500,
            delay: Math.min(index * 65, 1100),
            easing: 'ease-in-out',
            fill: 'forwards',
          }),
      );
      this.transitionTimer = setTimeout(() => this.clearTransition(), 2800);
    });
  }

  async toggleFullscreen(): Promise<void> {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch {
      this.notification.showError('امکان نمایش تمام‌صفحه وجود ندارد');
    }
  }

  setCategory(category: 'cabin' | 'outside'): void {
    this.captureTransition();
    this.category = category;
    this.page = 1;
  }

  private updateHubImage(record: ImageRecord, animate = true): void {
    const baseUrl = new URL(
      axiosJC261.defaults.baseURL || '/',
      window.location.href,
    ).origin;
    const image = normalizeImage(record, baseUrl);
    if (!image) return;
    const index = this.images.findIndex(
      (item) =>
        item.unitId === image.unitId && item.category === image.category,
    );
    if (index === -1) return;
    const previous = this.images[index];
    if (Date.parse(image.date) < Date.parse(previous.date)) return;
    if (!record.strFullName) image.name = previous.name;
    if (!record.strVehicleNo) image.plate = previous.plate;
    if (animate && previous.thumbnailUrl !== image.thumbnailUrl) {
      const visibleIndex = this.visibleImages.indexOf(previous);
      if (visibleIndex !== -1) this.captureTransition(visibleIndex);
    }
    this.images[index] = image;
    if (this.selectedImage === previous) this.selectedImage = image;
    this.lastUpdated = new Date();
  }

  async loadImages(): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    this.loadError = '';
    try {
      const response = await axiosJC261.post('GetLatestImages', {
        iSystemUser: Number(this.guard.decrypt(localStorage.getItem('ID'))),
        strSession: this.guard.decrypt(localStorage.getItem('Session')),
      });
      const result =
        typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;
      if (result?.ISSessionExpired === true) {
        this.notification.showError('نشست کاربری شما منقضی شده است');
        this.otherService.removeLocalStorage();
        this.router.navigate(['login'], { queryParams: { error: '1xzOPwsZ' } });
        return;
      }
      if (result?.iResult === 0)
        throw new Error(result.strError || 'دریافت تصاویر ناموفق بود');
      const rows = Array.isArray(result) ? result : [];
      if (!Array.isArray(rows))
        throw new Error('پاسخ دریافت تصاویر معتبر نیست');
      const baseUrl = new URL(
        axiosJC261.defaults.baseURL || '/',
        window.location.href,
      ).origin;
      const latest = new Map<string, MonitoringImage>();
      for (const row of rows as ImageRecord[]) {
        const image = normalizeImage(row, baseUrl);
        if (!image) continue;
        const key = `${image.unitId}|${image.category}`;
        const previous = latest.get(key);
        if (
          !previous ||
          (Date.parse(image.date) || 0) > (Date.parse(previous.date) || 0)
        )
          latest.set(key, image);
      }
      const nextImages = Array.from(latest.values());
      for (const image of nextImages) {
        if (!this.vehicleOrder.has(image.unitId))
          this.vehicleOrder.set(image.unitId, this.vehicleOrder.size);
      }
      nextImages.sort(
        (a, b) =>
          this.vehicleOrder.get(a.unitId)! - this.vehicleOrder.get(b.unitId)!,
      );
      const nextPage = Math.min(
        this.page,
        this.pageCountFor(nextImages, this.pinned),
      );
      this.captureIfVisibleChanges(nextImages, this.pinned, nextPage);
      this.images = nextImages;
      this.pendingHubImages.forEach((record) =>
        this.updateHubImage(record, false),
      );
      this.pendingHubImages = [];
      this.page = nextPage;
      this.lastUpdated = new Date();
      this.hasLoaded = true;
      this.schedulePageSizeUpdate();
    } catch (error) {
      this.loadError =
        error instanceof Error && error.message !== 'Network Error'
          ? error.message
          : 'اتصال به سرور تصاویر برقرار نشد';
      if (this.hasLoaded) this.notification.showError(this.loadError);
    } finally {
      this.pendingHubImages = [];
      this.loading = false;
    }
  }
}
